import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Send, Bot, User, Trash2, Loader2, Sparkles, SquarePen, ChevronDown, RefreshCw, GitFork, Play, Copy, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PageTransition } from '@/components/motion';
import Badge from '@/components/UI/Badge';
import ActionProposalCard from '@/components/Apollo/ActionProposalCard';
import ActionExecutionModal from '@/components/Apollo/ActionExecutionModal';
import { messagesAPI, brokersAPI, sessionsAPI, actionsAPI } from '@/services/apolloApi';
import { strategiesAPI } from '@/services/api';
import { useSession } from '@/lib/authClient';
import { useApollo } from '@/context/ApolloContext';

const SUGGESTION_CHIPS = [
  'How are my strategies performing?',
  'Analyze current market conditions',
  'Which strategy needs attention?',
  'Show portfolio summary',
];

const isSystemBroker = (b) => b?.config?.system === true;

const TICKER_RE = /\b([A-Z]{1,5}(?:\/USD[TC]?))\b/g;
const COMMON_WORDS = new Set([
  'I', 'A', 'THE', 'AND', 'OR', 'FOR', 'IS', 'IT', 'AT', 'TO', 'IN',
  'ON', 'OF', 'IF', 'BY', 'AS', 'AN', 'BE', 'DO', 'SO', 'NO', 'UP',
  'ALL', 'ANY', 'NOT', 'BUT', 'ARE', 'WAS', 'HAS', 'HAD', 'GET', 'SET',
  'RSI', 'SMA', 'ATR', 'API', 'USD', 'BB', 'UI', 'AI', 'OK',
]);

function extractReferences(text, allStrategies) {
  const strategies = [];
  const symbols = new Set();
  for (const s of allStrategies) {
    if (s.name && text.toLowerCase().includes(s.name.toLowerCase())) {
      strategies.push({ id: s.id, name: s.name, type: s.type });
    }
  }
  let match;
  while ((match = TICKER_RE.exec(text)) !== null) {
    const sym = match[1];
    if (!COMMON_WORDS.has(sym) && sym.length >= 2) symbols.add(sym);
  }
  for (const s of strategies) {
    const strat = allStrategies.find((a) => a.id === s.id);
    if (strat) {
      const params = typeof strat.parameters === 'string' ? JSON.parse(strat.parameters) : (strat.parameters || {});
      if (params.symbol) symbols.add(params.symbol);
    }
  }
  return { strategies, symbols: [...symbols] };
}

/**
 * Generate contextual follow-up suggestions based on the assistant's response.
 */
function generateSuggestions(content, allStrategies) {
  const suggestions = [];
  const lower = content.toLowerCase();

  // Strategy-specific follow-ups
  if (lower.includes('strategy') || lower.includes('performance')) {
    suggestions.push('Show detailed P&L breakdown');
  }
  if (lower.includes('grid') || lower.includes('bound') || lower.includes('level')) {
    suggestions.push('Adjust grid parameters');
  }
  if (lower.includes('risk') || lower.includes('loss') || lower.includes('drawdown')) {
    suggestions.push('Review risk management settings');
  }
  if (lower.includes('market') || lower.includes('price') || lower.includes('trend')) {
    suggestions.push('What are the current market conditions?');
  }
  if (lower.includes('trade') || lower.includes('order') || lower.includes('fill')) {
    suggestions.push('Show recent trade history');
  }
  if (lower.includes('rebalance') || lower.includes('adjust')) {
    suggestions.push('What changes would you recommend?');
  }

  // Generic fallbacks if nothing specific matched
  if (suggestions.length === 0) {
    suggestions.push('Tell me more', 'How can we improve this?');
  }

  // Always offer a summary option
  suggestions.push('Summarize key takeaways');

  // Limit to 3
  return suggestions.slice(0, 3);
}

const markdownComponents = {
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="list-disc ml-4 mb-2 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal ml-4 mb-2 space-y-0.5">{children}</ol>,
  li: ({ children }) => <li>{children}</li>,
  h1: ({ children }) => <h1 className="text-base font-bold mb-1 mt-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-sm font-bold mb-1 mt-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold mb-1 mt-1.5">{children}</h3>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  code: ({ inline, children, ...props }) => inline
    ? <code className="bg-white/10 px-1 py-0.5 rounded text-[12px] font-mono" {...props}>{children}</code>
    : <pre className="bg-black/30 border border-white/5 rounded-md p-3 overflow-x-auto mb-2 mt-1"><code className="text-[12px] font-mono leading-relaxed" {...props}>{children}</code></pre>,
  pre: ({ children }) => <>{children}</>,
  table: ({ children }) => <div className="overflow-x-auto mb-2"><table className="min-w-full text-xs border border-white/10 rounded">{children}</table></div>,
  th: ({ children }) => <th className="px-2 py-1 border-b border-white/10 text-left font-semibold bg-white/5">{children}</th>,
  td: ({ children }) => <td className="px-2 py-1 border-b border-white/5">{children}</td>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-primary/40 pl-3 italic text-muted-foreground mb-2">{children}</blockquote>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline">{children}</a>,
  hr: () => <hr className="border-white/10 my-2" />,
};

function MessageContent({ content, role }) {
  if (role === 'user') return <p className="whitespace-pre-wrap">{content}</p>;
  return <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{content}</ReactMarkdown>;
}

function ChatPanel() {
  const { brokerId, sessionId } = useParams();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const { setActiveBrokerId, setActiveSessionId, addLinkedItem, clearLinkedContext, linkedContext } = useApollo();
  const [broker, setBroker] = useState(null);
  const [brokers, setBrokers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [brokerPickerOpen, setBrokerPickerOpen] = useState(false);
  const [allStrategies, setAllStrategies] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  // Pending actions keyed by message ID
  const [pendingActions, setPendingActions] = useState({}); // { msgId: [action, ...] }
  const pendingActionsBuffer = useRef([]); // buffer during streaming
  const [executionModal, setExecutionModal] = useState(null); // { action, paramOverrides }
  const messagesEndRef = useRef(null);
  const brokerPickerRef = useRef(null);
  const apolloCreating = useRef(false);
  // Track resolved session during streaming (ref avoids render-cycle issues)
  const resolvedSessionRef = useRef(sessionId || null);

  // Find or create the system Apollo broker
  const ensureApolloBroker = useCallback(async () => {
    const allBrokers = await brokersAPI.getAll();
    let apollo = allBrokers.find(isSystemBroker);
    if (!apollo && !apolloCreating.current) {
      apolloCreating.current = true;
      try {
        apollo = await brokersAPI.create({ name: 'Apollo', autonomy: 'suggest', config: { system: true } });
        allBrokers.unshift(apollo);
      } catch (err) { console.error('Failed to create Apollo broker:', err); }
      finally { apolloCreating.current = false; }
    }
    // Migrate legacy observe system broker to suggest so it can propose changes
    if (apollo && apollo.autonomy === 'observe' && isSystemBroker(apollo)) {
      try {
        apollo = await brokersAPI.update(apollo.id, { autonomy: 'suggest' });
        const idx = allBrokers.findIndex((b) => b.id === apollo.id);
        if (idx >= 0) allBrokers[idx] = apollo;
      } catch { /* system broker may block edits — that's ok */ }
    }
    allBrokers.sort((a, b) => {
      if (isSystemBroker(a)) return -1;
      if (isSystemBroker(b)) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
    return { allBrokers, apollo };
  }, []);

  // Sync context IDs (synchronous — avoids setState-during-render warnings)
  useEffect(() => {
    setActiveBrokerId(brokerId || null);
    setActiveSessionId(sessionId || null);
    resolvedSessionRef.current = sessionId || null;
    return () => { setActiveBrokerId(null); setActiveSessionId(null); };
  }, [brokerId, sessionId, setActiveBrokerId, setActiveSessionId]);

  // Load brokers + ensure Apollo exists + resolve broker/session
  useEffect(() => {
    if (!session?.user?.id) return;
    if (streaming) return;
    let cancelled = false;

    (async () => {
      try {
        const { allBrokers, apollo } = await ensureApolloBroker();
        if (cancelled) return;
        setBrokers(allBrokers);

        const targetBrokerId = brokerId || apollo?.id;
        if (!targetBrokerId) { setLoading(false); return; }

        if (!brokerId && apollo) {
          navigate(`/apollo/chat/${apollo.id}`, { replace: true });
          return;
        }

        const b = await brokersAPI.getById(targetBrokerId);
        if (cancelled) return;
        setBroker(b);

        if (sessionId) {
          resolvedSessionRef.current = sessionId;
          const res = await messagesAPI.getAll(sessionId, { limit: 100 });
          if (cancelled) return;
          setMessages(res.messages || []);
        } else {
          const { sessions } = await sessionsAPI.getAll(targetBrokerId);
          if (cancelled) return;
          const active = sessions.find((s) => s.status === 'active');
          if (active) {
            navigate(`/apollo/chat/${targetBrokerId}/${active.id}`, { replace: true });
            return;
          }
          resolvedSessionRef.current = null;
          setMessages([]);
        }
      } catch (err) {
        console.error('ChatPanel init error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [session?.user?.id, brokerId, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cache strategies for reference extraction
  useEffect(() => {
    if (!session?.user?.id) return;
    strategiesAPI.getAll().then((res) => setAllStrategies(res.data || [])).catch(console.error);
  }, [session?.user?.id]);

  // Clear context on broker/session switch
  useEffect(() => { clearLinkedContext(); }, [brokerId, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Extract references from loaded messages
  const syncReferencesFromMessages = useCallback((msgs) => {
    if (allStrategies.length === 0) return;
    const recentAssistant = msgs.filter((m) => m.role === 'assistant').slice(-5);
    if (recentAssistant.length === 0) return;
    const combined = recentAssistant.map((m) => m.content).join('\n');
    const { strategies, symbols } = extractReferences(combined, allStrategies);
    for (const s of strategies) addLinkedItem('strategies', { id: s.id, name: s.name, type: s.type });
    for (const sym of symbols) addLinkedItem('symbols', { id: sym, name: sym });
  }, [allStrategies, addLinkedItem]);

  useEffect(() => {
    if (messages.length > 0 && allStrategies.length > 0) syncReferencesFromMessages(messages);
  }, [messages.length, allStrategies.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close broker picker on outside click
  useEffect(() => {
    if (!brokerPickerOpen) return;
    const handleClick = (e) => { if (brokerPickerRef.current && !brokerPickerRef.current.contains(e.target)) setBrokerPickerOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [brokerPickerOpen]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, streamingContent]);

  async function handleSend(e) {
    e.preventDefault();
    if (!input.trim() || sending || !brokerId) return;

    const content = input.trim();
    setInput('');
    setSending(true);
    setStreaming(true);
    setSuggestions([]);
    setStreamingContent('');

    const tempUserMsg = { id: `temp-${Date.now()}`, role: 'user', content, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, tempUserMsg]);

    const context = {
      strategies: (linkedContext.strategies || []).map((s) => ({ id: s.id, name: s.name, type: s.type })),
      symbols: (linkedContext.symbols || []).map((s) => s.name || s.id),
    };

    try {
      pendingActionsBuffer.current = [];
      await messagesAPI.stream(brokerId, resolvedSessionRef.current, content, context, {
        onSession: (data) => {
          if (data.id) {
            resolvedSessionRef.current = data.id;
          }
        },
        onUserMessage: (userMsg) => {
          setMessages((prev) => prev.map((m) => m.id === tempUserMsg.id ? userMsg : m));
        },
        onToken: (token) => { setStreamingContent((prev) => prev + token); },
        onToolResult: (data) => {
          // Detect pending actions from tool results
          try {
            const parsed = typeof data.content === 'string' ? JSON.parse(data.content) : data.content;
            if (parsed?.pending && parsed?.actionId) {
              pendingActionsBuffer.current.push(parsed);
            }
          } catch { /* not JSON or not an action */ }
        },
        onDone: (assistantMsg) => {
          setStreamingContent('');
          setStreaming(false);
          // Attach any pending actions to this message
          if (pendingActionsBuffer.current.length > 0) {
            setPendingActions((prev) => ({
              ...prev,
              [assistantMsg.id]: [...(prev[assistantMsg.id] || []), ...pendingActionsBuffer.current],
            }));
            pendingActionsBuffer.current = [];
          }
          setMessages((prev) => {
            const updated = [...prev, assistantMsg];
            const { strategies, symbols } = extractReferences(assistantMsg.content, allStrategies);
            for (const s of strategies) addLinkedItem('strategies', { id: s.id, name: s.name, type: s.type });
            for (const sym of symbols) addLinkedItem('symbols', { id: sym, name: sym });
            return updated;
          });
          setSuggestions(generateSuggestions(assistantMsg.content, allStrategies));
          const resolved = resolvedSessionRef.current;
          if (resolved && resolved !== sessionId) {
            navigate(`/apollo/chat/${brokerId}/${resolved}`, { replace: true });
          }
        },
        onError: (err) => {
          setStreamingContent('');
          setStreaming(false);
          setMessages((prev) => [...prev, { id: `err-${Date.now()}`, role: 'system', content: `Error: ${err.error || 'Stream failed'}`, created_at: new Date().toISOString() }]);
        },
      });
    } catch (err) {
      console.error('Stream failed:', err);
      setStreamingContent('');
      setStreaming(false);
      try {
        const result = await messagesAPI.send(brokerId, resolvedSessionRef.current, content);
        if (result.sessionId) {
          resolvedSessionRef.current = result.sessionId;
          navigate(`/apollo/chat/${brokerId}/${result.sessionId}`, { replace: true });
        }
        setMessages((prev) => [...prev.filter((m) => m.id !== tempUserMsg.id), result.userMessage, result.assistantMessage]);
        const { strategies, symbols } = extractReferences(result.assistantMessage.content, allStrategies);
        for (const s of strategies) addLinkedItem('strategies', { id: s.id, name: s.name, type: s.type });
        for (const sym of symbols) addLinkedItem('symbols', { id: sym, name: sym });
      } catch (fallbackErr) {
        setMessages((prev) => [...prev, { id: `err-${Date.now()}`, role: 'system', content: `Error: ${fallbackErr.message}`, created_at: new Date().toISOString() }]);
      }
    } finally {
      setSending(false);
    }
  }

  async function handleDeleteChat() {
    const sid = resolvedSessionRef.current || sessionId;
    if (!sid || !confirm('Delete this chat and all its messages?')) return;
    try {
      await sessionsAPI.delete(sid);
      // Navigate to broker root — will resolve to next active session or new chat
      navigate(`/apollo/chat/${brokerId}`);
    } catch (err) { console.error('Delete failed:', err); }
  }

  async function handleNewChat() {
    if (!brokerId) return;
    try {
      const newSession = await sessionsAPI.create(brokerId);
      navigate(`/apollo/chat/${brokerId}/${newSession.id}`);
    } catch (err) { console.error('Failed to create session:', err); }
  }

  function handleBrokerSelect(id) {
    setBrokerPickerOpen(false);
    navigate(`/apollo/chat/${id}`);
  }

  // Submit a suggestion chip as the user's next message
  function handleSubmitSuggestion(text) {
    setInput(text);
    setSuggestions([]);
    // Auto-submit on next tick so the input state is set
    setTimeout(() => {
      const form = document.querySelector('[data-chat-form]');
      if (form) form.requestSubmit();
    }, 0);
  }

  // Regenerate the last assistant response
  async function handleRegenerate(msgIndex) {
    if (sending || !brokerId) return;
    // Find the user message that preceded this assistant message
    const userMsg = messages.slice(0, msgIndex).reverse().find((m) => m.role === 'user');
    if (!userMsg) return;

    // Remove the assistant message and everything after it
    setMessages((prev) => prev.slice(0, msgIndex));
    setSuggestions([]);

    // Re-send the same user content
    setInput(userMsg.content);
    setTimeout(() => {
      const form = document.querySelector('[data-chat-form]');
      if (form) form.requestSubmit();
    }, 0);
  }

  // Fork: create a new session from a specific point in the conversation
  async function handleFork(msgIndex) {
    if (!brokerId) return;
    try {
      // Create a new session
      const newSession = await sessionsAPI.create(brokerId, 'Forked chat');

      // Send the conversation context up to this point as a system primer
      const contextMsgs = messages.slice(0, msgIndex + 1);
      const summary = contextMsgs.map((m) => `${m.role}: ${m.content.slice(0, 200)}`).join('\n');

      // Navigate to the new session and pre-populate with a context message
      navigate(`/apollo/chat/${brokerId}/${newSession.id}`);

      // Send a primer message so the new session has context
      setTimeout(async () => {
        try {
          await messagesAPI.send(brokerId, newSession.id,
            `[Forked from previous conversation]\n\nHere's the context so far:\n${summary}\n\nPlease continue from here.`
          );
        } catch { /* primer is best-effort */ }
      }, 500);
    } catch (err) { console.error('Fork failed:', err); }
  }

  // Copy message content to clipboard
  async function handleCopy(msgId, content) {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(msgId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch { /* clipboard API not available */ }
  }

  if (loading) {
    return (
      <PageTransition className="p-6">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </PageTransition>
    );
  }

  if (brokers.length === 0) {
    return (
      <PageTransition className="flex flex-col h-full items-center justify-center">
        <div className="text-center py-16 max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Welcome to Apollo</h2>
          <p className="text-sm text-muted-foreground mb-6">Create your first AI broker to start managing strategies with natural language.</p>
          <button onClick={() => navigate('/apollo/brokers/new')} className="px-6 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 transition-opacity">
            Create Your First Broker
          </button>
        </div>
      </PageTransition>
    );
  }

  const isApollo = isSystemBroker(broker);
  const userBrokers = brokers.filter((b) => !isSystemBroker(b));

  return (
    <PageTransition className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          {isApollo ? (
            <>
              <Sparkles className="w-5 h-5 text-primary" />
              <span className="font-semibold text-foreground">Apollo</span>
              <span className="text-[10px] text-muted-foreground/60">General Assistant</span>
            </>
          ) : (
            <>
              <Bot className="w-5 h-5 text-primary" />
              <span className="font-semibold text-foreground">{broker?.name || 'Chat'}</span>
              {broker && <Badge variant={broker.status === 'trading' ? 'success' : 'neutral'} className="text-[10px]">{broker.status}</Badge>}
              {linkedContext.strategies.length > 0 && (
                <span className="text-[10px] text-muted-foreground/60 ml-1">
                  {linkedContext.strategies.length} strategy{linkedContext.strategies.length !== 1 ? 'ies' : ''} linked
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={handleNewChat} className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent/10 transition-colors" title="New chat">
            <SquarePen className="w-4 h-4" />
          </button>
          {(sessionId || resolvedSessionRef.current) && (
            <button onClick={handleDeleteChat} className="p-2 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-400/10 transition-colors" title="Delete chat">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {messages.length === 0 && !streaming && (
          <div className="text-center py-12">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              {isApollo ? <Sparkles className="w-7 h-7 text-primary" /> : <Bot className="w-7 h-7 text-primary" />}
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-1">
              {isApollo ? "Hey, I'm Apollo" : 'New Conversation'}
            </h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              {isApollo
                ? 'Ask me anything — strategy questions, market analysis, portfolio review. Scope context in the sidebar for more targeted answers.'
                : 'Ask about strategy performance, request adjustments, or get market analysis.'}
            </p>
            {isApollo && userBrokers.length > 0 && (
              <div className="mb-6">
                <p className="text-[11px] text-muted-foreground/50 uppercase tracking-wider mb-2">Or scope to a broker</p>
                <div className="flex flex-wrap justify-center gap-2 max-w-lg mx-auto">
                  {userBrokers.map((b) => (
                    <button key={b.id} onClick={() => handleBrokerSelect(b.id)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10 hover:border-primary/30 hover:bg-primary/5 transition-colors">
                      <Bot className="w-3.5 h-3.5 text-primary" />
                      <span className="text-xs font-medium text-foreground">{b.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex flex-wrap justify-center gap-2 max-w-lg mx-auto">
              {SUGGESTION_CHIPS.map((chip) => (
                <button key={chip} onClick={() => setInput(chip)} className="px-3 py-1.5 rounded-full border border-white/10 text-xs text-muted-foreground hover:text-foreground hover:bg-accent/10 transition-colors">
                  {chip}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, idx) => {
          const isAssistant = msg.role === 'assistant';
          const isLastAssistant = isAssistant && !messages.slice(idx + 1).some((m) => m.role === 'assistant');

          return (
            <div key={msg.id}>
              <div className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role !== 'user' && (
                  <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    {isApollo ? <Sparkles className="w-3.5 h-3.5 text-primary" /> : <Bot className="w-3.5 h-3.5 text-primary" />}
                  </div>
                )}
                <div className={`max-w-[75%] rounded-xl px-4 py-2.5 text-sm ${
                  msg.role === 'user' ? 'bg-primary text-primary-foreground'
                    : msg.role === 'system' ? 'bg-amber-500/15 text-amber-300 border border-amber-500/20'
                    : 'bg-card border border-white/10 text-foreground'
                }`}>
                  <MessageContent content={msg.content} role={msg.role} />
                  <p className="text-[10px] opacity-50 mt-1">{new Date(msg.created_at).toLocaleTimeString()}</p>
                </div>
                {msg.role === 'user' && (
                  <div className="w-7 h-7 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <User className="w-3.5 h-3.5 text-foreground" />
                  </div>
                )}
              </div>

              {/* Action buttons for assistant messages */}
              {isAssistant && !sending && (
                <div className="flex items-center gap-0.5 ml-10 mt-1">
                  <button
                    onClick={() => handleCopy(msg.id, msg.content)}
                    className="p-1 rounded text-muted-foreground/30 hover:text-muted-foreground transition-colors"
                    title="Copy"
                  >
                    {copiedId === msg.id ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                  </button>
                  <button
                    onClick={() => handleRegenerate(idx)}
                    className="p-1 rounded text-muted-foreground/30 hover:text-muted-foreground transition-colors"
                    title="Regenerate"
                  >
                    <RefreshCw className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => handleFork(idx)}
                    className="p-1 rounded text-muted-foreground/30 hover:text-muted-foreground transition-colors"
                    title="Fork conversation from here"
                  >
                    <GitFork className="w-3 h-3" />
                  </button>
                  {isLastAssistant && (
                    <button
                      onClick={() => handleSubmitSuggestion('Continue')}
                      className="p-1 rounded text-muted-foreground/30 hover:text-muted-foreground transition-colors"
                      title="Resume / Continue"
                    >
                      <Play className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}

              {/* Pending action proposals */}
              {isAssistant && pendingActions[msg.id]?.length > 0 && (
                <div className="ml-10 mt-1">
                  {pendingActions[msg.id].map((action) => (
                    <ActionProposalCard
                      key={action.actionId}
                      action={action}
                      onAccept={async (act, overrides) => {
                        const isRunning = act.strategyStatus === 'running' || act.strategyStatus === 'analyzing';
                        if (isRunning && act.actionType === 'adjust_params') {
                          // Open execution modal for running strategy adjustment
                          setExecutionModal({ action: act, paramOverrides: overrides });
                        } else {
                          // Direct execution for stopped strategies or start/stop/pause actions
                          const payload = {};
                          if (act.actionType === 'adjust_params' && overrides) {
                            const currentParams = act.currentParams || {};
                            payload.payloadOverrides = { mergedParams: { ...currentParams, ...act.proposedChanges, ...overrides } };
                          }
                          await actionsAPI.approve(act.actionId, { ...payload, executeNow: true });
                        }
                      }}
                      onDecline={async (act) => {
                        await actionsAPI.reject(act.actionId);
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Suggested follow-ups after the last assistant message */}
              {isLastAssistant && suggestions.length > 0 && !sending && !streaming && (
                <div className="flex flex-wrap gap-1.5 ml-10 mt-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      onClick={() => handleSubmitSuggestion(s)}
                      className="px-2.5 py-1 rounded-full border border-white/10 text-[11px] text-muted-foreground hover:text-foreground hover:bg-accent/10 hover:border-white/20 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {streaming && streamingContent && (
          <div className="flex gap-3 justify-start">
            <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0 mt-0.5">
              {isApollo ? <Sparkles className="w-3.5 h-3.5 text-primary" /> : <Bot className="w-3.5 h-3.5 text-primary" />}
            </div>
            <div className="max-w-[75%] rounded-xl px-4 py-2.5 text-sm bg-card border border-white/10 text-foreground">
              <MessageContent content={streamingContent} role="assistant" />
              <span className="inline-block w-1.5 h-4 bg-primary/60 animate-pulse ml-0.5 align-text-bottom rounded-sm" />
            </div>
          </div>
        )}
        {streaming && !streamingContent && (
          <div className="flex gap-3 justify-start">
            <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0 mt-0.5">
              {isApollo ? <Sparkles className="w-3.5 h-3.5 text-primary" /> : <Bot className="w-3.5 h-3.5 text-primary" />}
            </div>
            <div className="rounded-xl px-4 py-2.5 bg-card border border-white/10">
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form data-chat-form onSubmit={handleSend} className="py-3 border-t border-white/10 flex justify-center">
      <div className="w-4/5">
        <div className="relative mb-2" ref={brokerPickerRef}>
          <button type="button" onClick={() => setBrokerPickerOpen(!brokerPickerOpen)} className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors">
            {isApollo ? <Sparkles className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
            <span>Chatting with <span className="font-medium text-foreground">{isApollo ? 'Apollo' : broker?.name}</span></span>
            <ChevronDown className={`w-3 h-3 transition-transform ${brokerPickerOpen ? 'rotate-180' : ''}`} />
          </button>
          {brokerPickerOpen && (
            <div className="absolute bottom-full left-0 mb-1 bg-card border border-white/10 rounded-lg shadow-xl py-1 min-w-[220px] z-20">
              {brokers.map((b) => {
                const isSys = isSystemBroker(b);
                return (
                  <button key={b.id} type="button" onClick={() => handleBrokerSelect(b.id)}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${b.id === brokerId ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-accent/10'}`}>
                    {isSys ? <Sparkles className="w-3 h-3 flex-shrink-0" /> : <Bot className="w-3 h-3 flex-shrink-0" />}
                    <span className="truncate">{b.name}</span>
                    {isSys
                      ? <span className="text-[9px] text-muted-foreground/50 ml-auto">General</span>
                      : <Badge variant={b.status === 'trading' ? 'success' : 'neutral'} className="text-[9px] ml-auto">{b.status}</Badge>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input type="text" value={input} onChange={(e) => setInput(e.target.value)}
            placeholder={isApollo ? 'Ask Apollo anything...' : 'Message your broker...'}
            className="flex-1 bg-accent/10 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            disabled={sending} />
          <button type="submit" disabled={!input.trim() || sending}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-40">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
      </form>

      {/* Execution modal for running strategy adjustments */}
      <ActionExecutionModal
        open={!!executionModal}
        onClose={() => setExecutionModal(null)}
        action={executionModal?.action}
        paramOverrides={executionModal?.paramOverrides}
      />
    </PageTransition>
  );
}

export default ChatPanel;
